import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import BodyLoader from "@components/BodyLoader";

// Puzzle-screen layout, in the cascade position pages/Solve.jsx used to give it
import "@css/Board.css";
import "@css/PuzzleLayout.css";
import "@css/Button.css";
import { parentsRoutes } from "@pages/parents/routes";
import { TokenProvider } from "@context/TokenProvider";
import { UserProvider } from "@context/UserContext";
import { ShopProvider } from "@context/ShopContext";
import { TournamentProvider } from "@context/TournamentContext";
import Header from "@components/Header";
import Notifications from "@components/Notifications";
import MaintenanceGate from "@components/MaintenanceGate";

import { QueryClientProvider } from "@tanstack/react-query";
import ErrorBoundary from "@components/ErrorBoundary";
import { queryClient } from '@context/QueryClient';
import { useAppUpdate } from "@hooks/useAppUpdate";

// Pages load on demand so the first visit does not download every admin screen.
const Profile = lazy(() => import("@components/Profile"));
const NotificationSettings = lazy(() => import("@components/NotificationSettings"));
const ProgramPage = lazy(() => import("@pages/ProgramPage"));
const Challenges = lazy(() => import("@components/Challenges"));
const Transactions = lazy(() => import("@components/Transactions"));
const Ranks = lazy(() => import("@components/Ranks"));
const CoordinatePage = lazy(() => import("@pages/CoordinatePage"));
const Tournaments = lazy(() => import("@components/Tournaments"));
const UsersPage = lazy(() => import("@components/admin-components/students/UsersPage"));
const CreateChallenge = lazy(() => import("@components/admin-components/CreateChallenge"));
const CreateNotification = lazy(() => import("@components/admin-components/CreateNotification"));
const AdminGuideTabs = lazy(() => import("@components/admin-components/Instruction"));
const AdminUserProfile = lazy(() => import("@components/admin-components/AdminProfile"));
const ChallengesList = lazy(() => import("@components/admin-components/ChallengesList"));
const ChallengeDetails = lazy(() => import("@components/admin-components/ChallengeDetails"));
const AutoChallenges = lazy(() => import("@components/admin-components/AutoChallenges"));
const Constructor = lazy(() => import("@components/admin-components/Constructor"));
const MetricsTimeline = lazy(() => import("@components/admin-components/MetricsTimeline"));
const Shop = lazy(() => import("@components/Shop"));
const Cart = lazy(() => import("@components/Cart"));
const UserOrders = lazy(() => import("@components/UserOrders"));
const ProductManager = lazy(() => import("@components/admin-components/ProductManager"));
const OrderManager = lazy(() => import("@components/admin-components/OrderManager"));
const ShopStatistics = lazy(() => import("@components/admin-components/ShopStatistics"));
const TransactionsAllUsers = lazy(() => import("@components/admin-components/AllTransactions"));
const AchievementsPage = lazy(() => import("@components/admin-components/Achievements"));
const LoginPage = lazy(() => import("@pages/LoginPage"));
const MaintenanceSettings = lazy(() => import("@components/admin-components/MaintenanceSettings"));
const SiteSettings = lazy(() => import("@components/admin-components/SiteSettings"));
const TournamentSettings = lazy(() => import("@components/admin-components/TournamentSettings"));
const TournamentAutomation = lazy(() => import("@components/admin-components/TournamentAutomation"));
const PuzzleSettings = lazy(() => import("@components/admin-components/PuzzleSettings"));
const FeatureFlagsSettingsPanel = lazy(() => import("@components/admin-components/FeatureFlagsSettingsPanel"));
const SplitSolutionsSettings = lazy(() => import("@components/admin-components/SplitSolutionsSettings"));
const StreakManager = lazy(() => import("@components/admin-components/StreakManager"));
const LevelsManager = lazy(() => import("@components/admin-components/LevelsManager"));
const ThemesManager = lazy(() => import("@components/admin-components/ThemesManager"));
const PuzzleImport = lazy(() => import("@components/admin-components/PuzzleImport"));
const MaintenancePage = lazy(() => import("@pages/MaintenancePage"));
const ProgramPuzzleRedirect = lazy(() => import("@pages/ProgramPuzzleRedirect"));
const EnterChallenge = lazy(() => import("@pages/EnterChallenge"));
const BillingSetupSuccess = lazy(() => import("@pages/BillingSetupSuccess"));
const ProgramChessInfo = lazy(() => import("@components/ProgramChessInfo"));
const ImpersonationUsers = lazy(() =>
    import("@components/admin-components/ImpersonationUsers").then(m => ({ default: m.ImpersonationUsers })),
);

const AppUpdateWatcher = () => {
    useAppUpdate();
    return null;
};

const App = () => {
    const location = window.location;
    return (
        <QueryClientProvider client={queryClient}>
            <AppUpdateWatcher />
            <TokenProvider>
                <UserProvider>
                    <ShopProvider>
                        <TournamentProvider>
                            <ErrorBoundary>
                                <Router>
                                    {location.pathname !== "/login" && <Header />}
                                    {location.pathname !== "/login" && <Notifications />}
                                    <MaintenanceGate>
                                        <Suspense fallback={<BodyLoader />}>
                                        <Routes>
                                            <Route path="/maintenance/" element={<MaintenancePage />} />
                                            <Route path="/" element={<Navigate to="/profile/" />} />
                                            <Route path="/profile/" element={<Profile />} />
                                            <Route path="/profile/notifications/" element={<NotificationSettings />} />

                                            <Route
                                                path="/profile/premium/"
                                                element={<Navigate to="/profile/?section=billing" replace />}
                                            />
                                            <Route
                                                path="/billing/setup/success/"
                                                element={<BillingSetupSuccess />}
                                            />
                                            {/* The admin mix-puzzle page was removed; old links go to the program */}
                                            <Route path="/solve/*" element={<Navigate to="/program/" replace />} />
                                            <Route path="/ranks/" element={<Ranks />} />
                                            <Route path="/program/" element={<ProgramPage />} />
                                            <Route
                                                path="/program/puzzle/:PuzzleId/"
                                                element={<ProgramPuzzleRedirect />}
                                            />
                                            <Route
                                                path="/program/:ThemeId/:PuzzleId/"
                                                element={<ProgramChessInfo />}
                                            />
                                            <Route path="/program/:ThemeId" element={<ProgramChessInfo />} />
                                            <Route path="/coordinate/" element={<CoordinatePage />} />
                                            <Route path="/challenges/" element={<Challenges />} />
                                            <Route path="/challenges/enter/:id/" element={<EnterChallenge />} />
                                            <Route path="/challenges/user" element={<Challenges />} />
                                            {parentsRoutes.map(({ path, Component }) => (
                                                <Route key={path} path={path} element={<Component />} />
                                            ))}
                                            <Route path="/transactions/" element={<Transactions />} />
                                            <Route
                                                path="/transactions/:id/"
                                                element={<Transactions />}
                                            />
                                            <Route
                                                path="/admin/transactions/all/"
                                                element={<TransactionsAllUsers />}
                                            />

                                            {/* Shop Routes */}
                                            <Route path="/shop/" element={<Shop />} />
                                            <Route path="/cart/" element={<Cart />} />
                                            <Route path="/orders/" element={<UserOrders />} />

                                            {/* Admin Shop Routes */}
                                            <Route path="/admin/products/" element={<ProductManager />} />
                                            <Route path="/admin/shop-orders/" element={<OrderManager />} />
                                            <Route path="/admin/shop-stats/" element={<ShopStatistics />} />
                                            <Route path="/admin/metrics/timeline/" element={<MetricsTimeline />} />

                                            <Route path="/tournaments/" element={<Tournaments />} />
                                            <Route path="/users/" element={<UsersPage />} />
                                            <Route
                                                path="/challenge/create/"
                                                element={<CreateChallenge />}
                                            />
                                            <Route
                                                path="/challenge/edit/:id"
                                                element={<CreateChallenge />}
                                            />
                                            <Route path="/instructions/" element={<AdminGuideTabs />} />
                                            <Route
                                                path="/admin/profile/:id/"
                                                element={<AdminUserProfile />}
                                            />
                                            <Route
                                                path="/admin/profile/:id/activity/"
                                                element={<AdminUserProfile />}
                                            />
                                            <Route
                                                path="/admin/challenges/"
                                                element={<ChallengesList />}
                                            />
                                            <Route
                                                path="/admin/challenges/:id/"
                                                element={<ChallengeDetails />}
                                            />
                                            <Route
                                                path="/admin/auto-challenges/"
                                                element={<AutoChallenges />}
                                            />
                                            <Route
                                                path="/admin/constructor/"
                                                element={<Constructor />}
                                            />
                                            <Route
                                                path="/admin/constructor/:ThemeId/:PuzzleId"
                                                element={<Constructor />}
                                            />
                                            <Route
                                                path="/admin/achievements/"
                                                element={<AchievementsPage />}
                                            />
                                            <Route
                                                path="/admin/notifications/"
                                                element={<CreateNotification />}
                                            />
                                            <Route
                                                path="/admin/site-settings/"
                                                element={<SiteSettings />}
                                            />
                                            <Route
                                                path="/admin/site-settings/maintenance/"
                                                element={<MaintenanceSettings />}
                                            />
                                            <Route
                                                path="/admin/site-settings/tournaments/"
                                                element={<TournamentSettings />}
                                            />
                                            <Route
                                                path="/admin/site-settings/tournament-automation/"
                                                element={<TournamentAutomation />}
                                            />
                                            <Route
                                                path="/admin/site-settings/puzzles/"
                                                element={<PuzzleSettings />}
                                            />
                                            <Route
                                                path="/admin/site-settings/split-solutions/"
                                                element={<SplitSolutionsSettings />}
                                            />
                                            <Route
                                                path="/admin/site-settings/feature-flags/"
                                                element={<FeatureFlagsSettingsPanel />}
                                            />
                                            <Route
                                                path="/admin/site-settings/levels/"
                                                element={<LevelsManager />}
                                            />
                                            <Route
                                                path="/admin/site-settings/themes/"
                                                element={<ThemesManager />}
                                            />
                                            <Route
                                                path="/admin/impersonation/"
                                                element={<ImpersonationUsers />}
                                            />
                                            <Route
                                                path="/admin/streaks/"
                                                element={<StreakManager />}
                                            />
                                            <Route
                                                path="/admin/puzzle-import/"
                                                element={<PuzzleImport />}
                                            />
                                            <Route path="/login" element={<LoginPage />} />

                                            <Route
                                                path="*"
                                                element={
                                                    <div>
                                                        404!!!!<a href="/">Домой</a>
                                                    </div>
                                                }
                                            />
                                        </Routes>
                                        </Suspense>
                                    </MaintenanceGate>
                                </Router>
                            </ErrorBoundary>
                        </TournamentProvider>
                    </ShopProvider>
                </UserProvider>
            </TokenProvider>
        </QueryClientProvider>
    );
};

export default App;
